import classNames from 'classnames';

import { assertUnreachable } from '../../../app/shared/common';
import ThreeDotsLoader from '../../../app/shared/components/Loaders/ThreeDotsLoader';
import { ExportPricelistToPdfModalTranslation } from '../../localization/ModuleTranslation';
import Button from '../../ui/Button/Button';
import { ModalBody, ModalFooter } from '../../ui/Modal/Modal';
import css from './ExportPricelistToPdfModalContent.css';
import type { ExportPricelistToPdfStatus } from './ExportPricelistToPdfStatus';

/**
 * The height a page is drawn at, in CSS pixels — what the preview rasterises to, and what the body
 * and the placeholder page are sized to. The stylesheet reads it as `--pageHeight`, set from here.
 */
export const PAGE_HEIGHT = 450;

interface ExportPricelistToPdfModalContentProps {
  status: ExportPricelistToPdfStatus;
  translation: ExportPricelistToPdfModalTranslation;
  /** The pages, once there are any to show; a placeholder page stands in until then. */
  children?: React.ReactNode;
  onDownload?: () => void;
}

const ExportPricelistToPdfModalContent = ({
  children,
  onDownload,
  status,
  translation,
}: ExportPricelistToPdfModalContentProps) => {
  const isLoading = status === 'loadingData' || status === 'pending';

  return (
    <>
      <ModalBody className={css.body} style={{ '--pageHeight': `${PAGE_HEIGHT}px` } as React.CSSProperties}>
        {children ?? <div className={css.placeholder}>{isLoading && <ThreeDotsLoader size="medium" />}</div>}
      </ModalBody>
      <ModalFooter className={css.footer}>
        <p role="status" className={classNames(css.status, { [css.error]: status === 'error' })}>
          {getStatusMessage(status, translation)}
        </p>
        <Button theme="primary" variant="solid" isDisabled={!onDownload} onClick={onDownload}>
          {translation.downloadLabel}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ExportPricelistToPdfModalContent;

function getStatusMessage(
  status: ExportPricelistToPdfStatus,
  translation: ExportPricelistToPdfModalTranslation,
): string {
  switch (status) {
    case 'loadingData':
      return translation.loadingDataLabel;
    case 'pending':
      return translation.generatingLabel;
    case 'success':
      return translation.readyLabel;
    case 'error':
      return translation.errorLabel;
    default:
      assertUnreachable(status);
  }
}
